import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useGetHistoryMoves } from 'modules/Room/Hooks/useGetHistoryMoves';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { MovesEnum } from 'shared/constants/shared-enums';

import emblem from 'assets/png/emblem.png?format=webp;avif&as=picture';

import { StreamMobileHistoryMove } from './stream-mobile-history-move';

interface StreamMobileHistoryMovesProps {
  move?: MovesEnum;
  currentRound?: number;
  isGameFinished?: boolean;
}

export const StreamMobileHistoryMoves = ({
  move,
  currentRound,
  isGameFinished,
}: StreamMobileHistoryMovesProps) => {
  const gameHistory = useGetHistoryMoves(currentRound, true);
  const historyRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);
  const { activeRoomId } = useParams();

  const reversedHistory = [...gameHistory].reverse();
  const visibleRounds = reversedHistory.filter((_, i) => {
    return (currentRound && i < currentRound - 1) || isGameFinished;
  });

  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is fully rendered before measuring
    const checkScrollable = () => {
      if (historyRef.current) {
        // Calculate total width of all children
        const children = Array.from(historyRef.current.children);
        let totalChildrenWidth = 0;

        children.forEach((child) => {
          // Include the child's width plus any margins
          const childStyles = window.getComputedStyle(child);
          const marginLeft = parseInt(childStyles.marginLeft || '0', 10);
          const marginRight = parseInt(childStyles.marginRight || '0', 10);
          totalChildrenWidth += child.getBoundingClientRect().width + marginLeft + marginRight;
        });

        const containerStyles = window.getComputedStyle(historyRef.current);
        const gap = parseInt(containerStyles.columnGap || '0', 10);
        const gapWidth = children.length > 1 ? (children.length - 1) * gap : 0;
        totalChildrenWidth += gapWidth;

        const containerWidth = historyRef.current.clientWidth;

        setIsScrollable(totalChildrenWidth > containerWidth);
      }
    };

    requestAnimationFrame(checkScrollable);
  }, [gameHistory]);

  // Effect to scroll to show last move
  useEffect(() => {
    if (historyRef?.current && isScrollable) {
      historyRef.current.scrollTo({
        left: historyRef.current.scrollWidth,
        behavior: 'smooth',
      });
    }
  }, [visibleRounds.length, isScrollable]);

  if (!activeRoomId) return null;

  return (
    <div className="flex h-20 w-full justify-center sm:h-[132px]">
      <div className="flex w-full items-center">
        <div
          ref={historyRef}
          className={`no-scrollbar flex h-full w-full items-center gap-2 overflow-x-scroll whitespace-nowrap ${!isScrollable ? 'justify-end' : ''} `}
        >
          {visibleRounds.length !== 0 ? (
            visibleRounds.map((round, index) => {
              const isLast = index === visibleRounds.length - 1;

              return (
                <StreamMobileHistoryMove
                  key={round.encrMoveA + index}
                  round={round}
                  show
                  index={index}
                  totalVisible={visibleRounds.length}
                  isLast={isLast}
                />
              );
            })
          ) : (
            <>
              <div className="h-[54px] w-[2px] rounded-full bg-indigo sm:h-[108px]" />

              <div
                className="h-[54px] w-[40px] rounded-[12px] p-[1px]"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(50, 46, 102, 1) 0%, rgba(55, 52, 112, 1) 100%)',
                }}
              >
                <div
                  className="relative flex h-full w-full shrink-0 items-center justify-center overflow-hidden rounded-[12px]"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(21, 23, 40, 1) 0%, rgba(29, 33, 62, 1) 100%)',
                  }}
                >
                  <OptimizedImage
                    alt="emblem"
                    className="h-[36px] w-[36px]"
                    style={{ objectFit: 'cover' }}
                    imageData={emblem}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
